import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { UISchema, ScreenMetadata } from '@types/index';
import { screenManager, schemaLoader } from '@ui-engine/index';

interface UIContextValue {
  currentScreen: ScreenMetadata | null;
  screens: ScreenMetadata[];
  isLoading: boolean;
  error: string | null;
  loadScreen: (screenId: string) => Promise<void>;
  loadScreenFromURL: (url: string) => Promise<void>;
  registerScreen: (screen: ScreenMetadata) => boolean;
  navigateToScreen: (screenId: string) => Promise<boolean>;
  executeAction: (actionId: string, context?: any) => void;
  clearError: () => void;
}

const UIContext = createContext<UIContextValue | undefined>(undefined);

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentScreen, setCurrentScreen] = useState<ScreenMetadata | null>(null);
  const [screens, setScreens] = useState<ScreenMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerScreen = useCallback((screen: ScreenMetadata): boolean => {
    const success = screenManager.registerScreen(screen);
    if (success) {
      setScreens((prev) => [...prev, screen]);
    } else {
      setError('Failed to register screen');
    }
    return success;
  }, []);

  const loadScreen = useCallback(async (screenId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const screen = screenManager.getScreen(screenId);
      if (!screen) {
        throw new Error(`Screen "${screenId}" not found`);
      }

      screenManager.setCurrentScreen(screenId);
      setCurrentScreen(screen);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load screen';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadScreenFromURL = useCallback(async (url: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const screen = await schemaLoader.loadFromURL(url);
      registerScreen(screen);

      screenManager.setCurrentScreen(screen.id);
      setCurrentScreen(screen);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load screen from URL';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [registerScreen]);

  const navigateToScreen = useCallback(async (screenId: string): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);
      
      let screen = screenManager.getScreen(screenId);
      
      // If screen not found, try to load it or create it
      if (!screen) {
        // For custom component screens (course_screen, lab_screen), create metadata without schema
        if (screenId === 'course_screen' || screenId === 'lab_screen' || screenId === 'user_management_screen') {
          if(screenId === 'user_management_screen') {
            screen = {
            id: screenId,
            name: 'User Management',
            title: 'User Management',
            layout: 'single',
            schema: { type: 'container' } as any, // Minimal schema for component-based screens
          };
        }
        else 
          screen = {
            id: screenId,
            name: screenId === 'course_screen' ? 'Create Course' : 'LLM Lab',
            title: screenId === 'course_screen' ? 'Create Course' : 'LLM Lab',
            layout: 'single',
            schema: { type: 'container' } as any, // Minimal schema for component-based screens
          };
          // Register the screen
          const registered = registerScreen(screen);
          if (!registered) {
            console.warn(`Could not register screen ${screenId}, but proceeding`);
          }
        } else {
          try {
            // Convert screen ID to schema filename (e.g., 'dashboard_screen' -> 'dashboard.json')
            const schemaName = screenId.replace('_screen', '');
            const url = `/schemas/${schemaName}.json`;
            screen = await schemaLoader.loadFromURL(url);
            registerScreen(screen);
          } catch (loadErr) {
            console.error(`Failed to load screen ${screenId}:`, loadErr);
            setError(`Screen "${screenId}" not found`);
            setIsLoading(false);
            return false;
          }
        }
      }

      screenManager.setCurrentScreen(screenId);
      setCurrentScreen(screen);
      setIsLoading(false);
      return true;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Navigation failed';
      console.error('Navigation error:', err);
      setError(errorMsg);
      setIsLoading(false);
      return false;
    }
  }, [registerScreen]);

  const executeAction = useCallback((actionId: string, context?: any) => {
    try {
      setError(null);
      screenManager.executeAction(actionId, context, {
        onNavigate: navigateToScreen,
        onEvent: (eventName, payload) => {
          console.log('Event triggered:', eventName, payload);
        },
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Action execution failed';
      setError(errorMsg);
    }
  }, [navigateToScreen]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <UIContext.Provider
      value={{
        currentScreen,
        screens,
        isLoading,
        error,
        loadScreen,
        loadScreenFromURL,
        registerScreen,
        navigateToScreen,
        executeAction,
        clearError,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextValue => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
};
